// Step 5d Phase 5 — staleness advisory framework (design v2 §8).
//
// ONE central place for the op-type → staleness-class map, the class thresholds, the §8
// inventory age buckets, and the per-action evaluation. The four enqueueOp call sites and
// the cache-inventory screen ALL import from here — they must never hand-roll a threshold,
// so the policy can't drift. Advisories are ADVISORY, never blocking (the standing
// advisory-not-blocking lens): they inform, the op queues unchanged, the firing is recorded.

import type { QueuedOp } from './db';

export type StalenessClass = 'A' | 'B' | 'none';

// §8 thresholds. Class A = 2 h (availability can't be confirmed); Class B = 24 h (softer).
export const CLASS_THRESHOLD_MS: Record<'A' | 'B', number> = {
  A: 2 * 60 * 60 * 1000,
  B: 24 * 60 * 60 * 1000,
};

// op_type → class. `satisfies Record<QueuedOp['op_type'], …>` is the exhaustiveness guard:
// if a new op_type is ever added to the union without a class here, tsc FAILS the build
// (D-§A requirement). §3.3 puts waitlist join AND leave at Class A; cancel at B; maintenance
// observed-facts at none.
export const STALENESS_CLASS = {
  booking_create:     'A',
  booking_edit:       'A',
  waitlist_join:      'A',
  waitlist_leave:     'A',
  relocation:         'A',
  booking_cancel:     'B',
  maintenance_return: 'none',
} satisfies Record<QueuedOp['op_type'], StalenessClass>;

export function classForOp(opType: QueuedOp['op_type']): StalenessClass {
  return STALENESS_CLASS[opType];
}

export interface StalenessAdvice {
  class: 'A' | 'B';
  ageMs: number;
}

// Evaluate whether a per-action advisory should fire for an op queued now.
// `readStamp` = the bookings-scope refreshed_at captured at action time (getBookingsReadStamp).
// Returns the advice (class + observed age) when the data is older than the op's class
// threshold; null when no advisory is warranted (fresh data, online, or a `none`-class op).
// A null/absent read_stamp means we have no age to judge (online action / never-cached) → no
// advisory. `now` is injectable for deterministic unit tests.
export function evaluateStaleness(
  opType: QueuedOp['op_type'],
  readStamp: string | null | undefined,
  now: number = Date.now(),
): StalenessAdvice | null {
  const cls = classForOp(opType);
  if (cls === 'none') return null;
  if (!readStamp) return null;
  const stamp = new Date(readStamp).getTime();
  if (Number.isNaN(stamp)) return null;
  const ageMs = now - stamp;
  if (ageMs <= CLASS_THRESHOLD_MS[cls]) return null; // within threshold → no advisory
  return { class: cls, ageMs };
}

// Central entry point for the four enqueueOp sites: given an op type + the bookings-scope
// read_stamp, return BOTH the lens record to stamp on the queued op AND the inline,
// non-blocking advisory message to surface (null when no advisory is warranted). Sites must
// import this — they must not hand-roll the class/threshold (D-§A: single source).
export function stalenessFor(
  opType: QueuedOp['op_type'],
  readStamp: string | null | undefined,
  scopeLabel: string = 'the booking schedule',
  now: number = Date.now(),
): { record?: QueuedOp['staleness_advised']; message: string | null } {
  const advice = evaluateStaleness(opType, readStamp, now);
  if (!advice) return { message: null };
  return { record: { class: advice.class, age_ms: advice.ageMs }, message: advisoryMessage(advice, scopeLabel) };
}

// §8 advisory wording. Class A: availability can't be confirmed; Class B: softer 24 h framing.
export function advisoryMessage(advice: StalenessAdvice, scopeLabel: string): string {
  const age = formatAgeShort(advice.ageMs);
  if (advice.class === 'A') {
    return `Your data for ${scopeLabel} is ${age} old — availability can't be confirmed until you reconnect.`;
  }
  return `Your data for ${scopeLabel} is ${age} old — it may have changed since you were last online.`;
}

// ── Inventory age buckets (cache-inventory colour code) ─────────────────────────
// Anchored on the §8 thresholds (2 h / 24 h) with a 30-min "approaching" warning band, so
// the colours and the per-action advisories tell one consistent story:
//   fresh         < 90 min        — comfortably within Class A
//   approaching_2h 90 min … < 2 h  — Class A advisory imminent
//   past_2h        2 h … < 24 h    — past Class A (≥ exactly 2 h), within Class B
//   past_24h       ≥ 24 h          — past Class B
export type AgeBucket = 'fresh' | 'approaching_2h' | 'past_2h' | 'past_24h';
const APPROACHING_MS = 90 * 60 * 1000;

export function ageBucket(ageMs: number): AgeBucket {
  if (ageMs >= CLASS_THRESHOLD_MS.B) return 'past_24h';
  if (ageMs >= CLASS_THRESHOLD_MS.A) return 'past_2h';
  if (ageMs >= APPROACHING_MS) return 'approaching_2h';
  return 'fresh';
}

// Colour + short label per bucket (consumed by the inventory screen).
export const BUCKET_DISPLAY: Record<AgeBucket, { color: string; label: string }> = {
  fresh:          { color: '#16a34a', label: 'fresh' },          // green
  approaching_2h: { color: '#d97706', label: 'approaching 2 h' }, // amber
  past_2h:        { color: '#ea580c', label: 'over 2 h' },        // orange
  past_24h:       { color: '#dc2626', label: 'over 24 h' },       // red
};

// Compact age formatter (mirrors OfflineDataBanner's style for consistency).
export function formatAgeShort(ms: number): string {
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'moments';
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) {
    const rem = mins % 60;
    return rem > 0 ? `${hours} h ${rem} min` : `${hours} h`;
  }
  return `${Math.floor(hours / 24)} days`;
}
